import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { categoryService, CategoryDto, PagedResult } from '../../services/categoryService';
import { getFullImageUrl } from '@/lib/api/apiClient';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Eye, EyeOff, FolderTree, Power, ChevronDown, ChevronUp, UserPlus, UserMinus } from 'lucide-react';

type EnableForNewUsersFilter = 'all' | 'yes' | 'no';

export const CategoryList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<PagedResult<CategoryDto> | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [enableForNewUsersFilter, setEnableForNewUsersFilter] = useState<EnableForNewUsersFilter>('all');
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    // تأخير بسيط لضمان اكتمال rehydration من localStorage قبل طلب الفئات
    const t = setTimeout(() => {
      fetchCategories(false);
    }, 150);
    return () => clearTimeout(t);
  }, [includeInactive, enableForNewUsersFilter]);

  const fetchCategories = async (isRetry = false) => {
    try {
      setLoading(true);
      const enableForNewUsers =
        enableForNewUsersFilter === 'all' ? undefined : enableForNewUsersFilter === 'yes';
      const data = await categoryService.getAllCategories({
        includeInactive,
        enableForNewUsers,
        pageNumber: 1,
        pageSize: 50,
      });
      setCategories(data);
    } catch (error: any) {
      const status = error.response?.status;
      const data = error.response?.data;
      const msg = data?.message ?? data?.Message;
      if (status === 401) {
        if (!isRetry && retryCount < 1) {
          setRetryCount((c) => c + 1);
          setTimeout(() => fetchCategories(true), 400);
          return;
        }
        toast.error(msg || 'سجّل الدخول أولاً (مثلاً admin@test.com / Admin123).');
        navigate('/admin/login', { replace: true });
      } else if (status === 403) {
        toast.error(msg || 'ليس لديك صلاحية. تحتاج دور أدمن أو محرر محتوى.');
      } else {
        if (!isRetry && retryCount < 1 && !error.response) {
          setRetryCount((c) => c + 1);
          setTimeout(() => fetchCategories(true), 400);
          return;
        }
        const detail = data?.detail ?? data?.Detail;
        const serverMsg = msg || detail || (status === 500 ? 'خطأ في الخادم. راجع السجلات.' : 'فشل تحميل الفئات.');
        toast.error(serverMsg, { duration: 8000 });
      }
      console.error('Failed to fetch categories:', error?.response?.data ?? error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id: string, _currentStatus: boolean) => {
    try {
      const newStatus = await categoryService.toggleCategoryStatus(id);
      toast.success(`تم ${newStatus ? 'تفعيل' : 'تعطيل'} الفئة بنجاح`);
      fetchCategories();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل تغيير حالة الفئة');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`هل أنت متأكد من حذف الفئة "${name}"؟`)) {
      return;
    }

    try {
      await categoryService.deleteCategory(id);
      toast.success('تم حذف الفئة بنجاح');
      fetchCategories();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل حذف الفئة');
    }
  };

  const handleBulkEnableForNewUsers = async (enable: boolean) => {
    if (filteredCategories.length === 0) {
      toast.error('لا توجد فئات معروضة لتحديثها');
      return;
    }
    try {
      setBulkLoading(true);
      const ids = filteredCategories.map((c) => c.id);
      const { updatedCount } = await categoryService.bulkUpdateEnableForNewUsers(ids, enable);
      toast.success(`تم تحديث ${updatedCount} فئة`);
      fetchCategories();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل التحديث الجماعي');
    } finally {
      setBulkLoading(false);
    }
  };

  const filteredCategories = categories?.items.filter((cat) =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cat.nameAr && cat.nameAr.includes(searchTerm))
  ) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-accent-dark mb-2">إدارة الفئات</h1>
          <p className="text-accent-dark/70">إدارة وتنظيم فئات الأسئلة</p>
        </div>
        <Link to="/admin/categories/create">
          <Button className="bg-secondary hover:bg-secondary-dark text-white">
            <Plus className="w-4 h-4 mr-2" />
            إضافة فئة جديدة
          </Button>
        </Link>
      </div>

      {/* Filters - Collapsible */}
      <Card className="border-2 border-accent-greenLight/20">
        <button
          type="button"
          onClick={() => setFiltersCollapsed(!filtersCollapsed)}
          className="w-full p-4 flex items-center justify-between text-right hover:bg-accent-greenLight/5 transition-colors rounded-t-lg"
        >
          <span className="font-medium text-accent-dark">الفلاتر والعمليات الجماعية</span>
          {filtersCollapsed ? (
            <ChevronDown className="w-5 h-5 text-accent-dark/60" />
          ) : (
            <ChevronUp className="w-5 h-5 text-accent-dark/60" />
          )}
        </button>
        {!filtersCollapsed && (
          <CardContent className="p-4 pt-0 border-t border-accent-greenLight/10">
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap gap-3 items-center">
                <div className="flex-1 min-w-[200px] relative">
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-accent-dark/50 w-5 h-5" />
                  <Input
                    placeholder="بحث في الفئات..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pr-10"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => setIncludeInactive(!includeInactive)}
                  className="border-accent-greenLight/30"
                >
                  {includeInactive ? (
                    <>
                      <Eye className="w-4 h-4 mr-2" />
                      إخفاء المعطلة
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-4 h-4 mr-2" />
                      عرض المعطلة
                    </>
                  )}
                </Button>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-accent-dark/70">تفعيل للمستخدمين الجدد:</span>
                  <div className="flex gap-1">
                    {(['all', 'yes', 'no'] as const).map((value) => (
                      <Button
                        key={value}
                        variant={enableForNewUsersFilter === value ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setEnableForNewUsersFilter(value)}
                        className={
                          enableForNewUsersFilter === value
                            ? 'bg-secondary hover:bg-secondary-dark text-white'
                            : 'border-accent-greenLight/30'
                        }
                      >
                        {value === 'all' ? 'الكل' : value === 'yes' ? 'مفعّل' : 'غير مفعّل'}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
              {filteredCategories.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-accent-greenLight/10">
                  <span className="text-sm text-accent-dark/70 self-center">عمليات على المعروضة ({filteredCategories.length}):</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={bulkLoading}
                    onClick={() => handleBulkEnableForNewUsers(true)}
                    className="border-accent-greenLight/30"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    تفعيل الكل للمستخدمين الجدد
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={bulkLoading}
                    onClick={() => handleBulkEnableForNewUsers(false)}
                    className="border-accent-greenLight/30"
                  >
                    <UserMinus className="w-4 h-4 mr-1" />
                    إلغاء تفعيل الكل
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Categories Grid */}
      {filteredCategories.length === 0 ? (
        <Card className="border-2 border-accent-greenLight/20">
          <CardContent className="p-12 text-center">
            <FolderTree className="w-16 h-16 mx-auto mb-4 text-accent-dark/30" />
            <p className="text-lg text-accent-dark/70 mb-2">لا توجد فئات</p>
            <p className="text-sm text-accent-dark/50 mb-4">
              {searchTerm ? 'لم يتم العثور على فئات تطابق البحث' : 'ابدأ بإضافة فئة جديدة'}
            </p>
            <Link to="/admin/categories/create">
              <Button className="bg-secondary hover:bg-secondary-dark text-white">
                <Plus className="w-4 h-4 mr-2" />
                إضافة فئة جديدة
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredCategories.map((category) => (
            <Card
              key={category.id}
              className="border border-accent-greenLight/20 hover:shadow-md transition-all duration-300 hover:border-accent-greenLight/40"
            >
              <CardContent className="p-4">
                <div className="flex flex-col items-center text-center space-y-3">
                  {/* أيقونة الفئة - مسار الصورة من API (يعمل على أي جهاز عبر VITE_API_URL) */}
                  <div className="relative w-20 h-20 rounded-xl overflow-hidden bg-gray-50 flex items-center justify-center">
                    <FolderTree className="w-10 h-10 text-accent-dark/30" />
                    {category.imageUrl && getFullImageUrl(category.imageUrl) && (
                      <img
                        src={getFullImageUrl(category.imageUrl)}
                        alt={category.name}
                        className="absolute inset-0 w-full h-full object-contain"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                  </div>
                  
                  {/* اسم الفئة */}
                  <div>
                    <h3 className="font-bold text-accent-dark text-base leading-tight">
                      {category.nameAr || category.name}
                    </h3>
                    <div className="flex flex-wrap justify-center gap-1 mt-1">
                      {category.sectionName && (
                        <span className="px-2 py-0.5 text-[10px] bg-accent-greenLight/20 text-accent-greenDark rounded">
                          {category.sectionName}
                        </span>
                      )}
                      {category.enableForNewUsers && (
                        <span className="px-2 py-0.5 text-[10px] bg-secondary/20 text-secondary rounded">
                          للمستخدمين الجدد
                        </span>
                      )}
                      {!category.isActive && (
                        <span className="px-2 py-0.5 text-[10px] bg-accent-red/10 text-accent-red rounded">
                          معطلة
                        </span>
                      )}
                    </div>
                  </div>

                  {/* معلومات مختصرة */}
                  <div className="text-xs text-accent-dark/50">
                    {category.questionCount ?? 0} سؤال
                  </div>

                  {/* أزرار التحكم */}
                  <div className="flex gap-1 w-full pt-2 border-t border-accent-greenLight/10">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-1 h-8 text-xs hover:bg-accent-yellowGreen/20 px-2"
                      onClick={() => handleToggle(category.id, category.isActive)}
                    >
                      <Power className={`w-3 h-3 ${category.isActive ? 'text-secondary' : 'text-accent-dark/40'}`} />
                    </Button>
                    <Link to={`/admin/categories/edit/${category.id}`} className="flex-1">
                      <Button variant="ghost" size="sm" className="w-full h-8 text-xs hover:bg-accent-greenLight/20 px-2">
                        <Edit className="w-3 h-3" />
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-1 h-8 text-xs hover:bg-accent-red/10 text-accent-red px-2"
                      onClick={() => handleDelete(category.id, category.nameAr || category.name)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {categories && categories.totalPages > 1 && (
        <Card className="border-2 border-accent-greenLight/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-accent-dark/70">
                عرض {categories.items.length} من {categories.totalCount} فئة
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled>
                  السابق
                </Button>
                <span className="px-4 py-2 text-sm text-accent-dark">
                  صفحة {categories.pageNumber} من {categories.totalPages}
                </span>
                <Button variant="outline" size="sm" disabled>
                  التالي
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

